/* eslint-disable no-use-before-define */
import warning from "warning";
import update from "react-addons-update";
import { findDOMNode } from "react-dom";
import {
  debounce,
  intersection,
  findIndex,
  isString,
  isNil,
  isEmpty,
  omit,
} from "lodash";
import { format4Params, format4SetValue } from "./transformUtils";
import { isValueInOptions } from "../../utils";

require("raf/polyfill");

function freezeStateData(mutableStates, state, simple) {
  const data = state.toJS();
  const output = { componentSchema: state.componentSchema };
  mutableStates.forEach(({ name, formatOut }) => {
    if (formatOut === false) return;
    let value = data[name];
    if (typeof formatOut === "function") {
      value = formatOut(value, data, output, simple, state.get(name));
    }
    output[name] = value;
  });
  return output;
}

function calculateBehavior(behavior) {
  const { fieldBehavior } = behavior || {};
  if (isNil(fieldBehavior)) {
    return null;
  } else if (fieldBehavior === "" || fieldBehavior === "NORMAL") {
    return { readonly: false, disabled: false, visible: true };
  } else if (fieldBehavior === "READONLY") {
    return { readonly: true, disabled: false };
  } else if (fieldBehavior === "DISABLED") {
    return { readonly: false, disabled: true };
  } else if (fieldBehavior === "HIDDEN") {
    return { visible: false };
  } else {
    return null;
  }
}

/**
 * 获取 fieldApi 字段操作工具集
 */
export default (options = {}) => {
  const { context, endpoint, validationMap } = options;

  // 收集数据过程置为真值，避免引起联动
  let disableEventHandler = false;

  /**
   * 判断字段是否存在
   * @param {string} fieldId 字段id
   */
  const isFieldExist = (fieldId) => {
    const state = context.store.get(fieldId);
    return !!state;
  };

  /**
   * 获取 visual-state
   * @param {string} fieldId 字段id
   */
  const getFieldState = (fieldId) => {
    const state = context.store.get(fieldId);
    /** 由上游保证 fieldId 存在 */
    warning(state, `fieldId:${fieldId} is not existed in the page!`);
    return state;
  };

  /**
   * 获取字段的值
   * @param {string} fieldId 字段id
   * @param {boolean} convertFlag 是否需要将视图数据转换成模型数据
   */
  const getFieldValue = (fieldId, convertFlag = false) => {
    const state = getFieldState(fieldId);
    const value = state.getVal();
    if (value && convertFlag) {
      const componentName = getComponentName(fieldId);
      return format4Params(value, componentName, fieldId);
    }
    if (state.fieldDataFormatter) {
      return state.fieldDataFormatter(value);
    }
    return value;
  };

  /**
   * 获取组件类型
   * @param {string} fieldId 字段id
   */
  const getComponentName = (fieldId) => {
    const state = getFieldState(fieldId);
    return state.cacheData.componentName;
  };

  /**
   * 获取组件 schema，state.componentSchema 不是实时数据
   * @param {string} fieldId 字段id
   */
  const getComponentSchema = (fieldId) => {
    const state = getFieldState(fieldId);
    return state && state.componentSchema;
  };

  /**
   * 获取组件实例
   * @param {string} fieldId 字段id
   */
  const getComponentInstance = (fieldId) => {
    return context.getComponentInstance(fieldId);
  };

  /**
   * 字段赋值
   * @param {string} fieldId 字段id
   * @param {any} value 值
   * @param {boolean} convertFlag 是否需要将模型数据转换成视图数据
   * convertFlag = true 时，可应用于使用模型数据的 schema-request, schema-rule 规则
   */
  const setFieldValue = (fieldId, value, option = {}) => {
    const state = getFieldState(fieldId);
    const { dataShare } = option;

    // null 或 undefined 清空
    if (value === undefined || value === null) {
      resetField(fieldId);
      return;
    }

    const realValue = format4SetValue(value, state, dataShare);
    state.setVal(realValue);
  };

  /**
   * 值重置，清空
   * 场景一：Request 异步请求获取下拉框选项，导致下拉框值需要清空，禁用校验
   * 场景二：Rules 联动清空输入框的值时，启用校验
   * @param {string} fieldId 字段id
   * @param {boolean} disableValid 禁用校验
   */
  const resetField = (fieldId, disableValid = true) => {
    const state = getFieldState(fieldId);

    if (disableValid) state.disableValid();
    state.setVal(null);
    if (disableValid) state.enableValid();
  };

  /**
   * 监听表单控件值变更
   * @param {string} fieldId 字段id
   * @param {function} handler 回调
   */
  const onValueChange = (
    fieldId,
    handler,
    { ignoreError = false, filter = null }
  ) => {
    const state = getFieldState(fieldId);
    const schema = getComponentSchema(fieldId);
    let preVal = state.getVal();
    const realIgnoreError =
      ignoreError || (schema && schema.componentName === "Card");

    const innerHandler = () => {
      if (disableEventHandler) return;
      // 静默校验
      const data = freezeStateData(state.mutableStates, state, true);
      if (!realIgnoreError && data.hasError) return;

      // 卡片组件，配置了filter后，仅有新增/删除，或者 changedKey 处于filter.subId时才会触发值变化
      const changedKey = data.changedKey;
      if (
        filter &&
        filter.subId &&
        changedKey &&
        filter.subId.indexOf(changedKey.subId) === -1
      ) {
        return;
      }

      // 不是值变更，退出
      // 重置表单项时，keys 为空
      const value = state.getVal();
      if (JSON.stringify(preVal) === JSON.stringify(value)) return;

      preVal = value;
      handler({ value, changedKey });
    };

    const delayTime = schema && schema.componentName === "Card" ? 5 : 500;

    return state.onChange(debounce(innerHandler, delayTime));
  };

  const onValidationChange = (fieldId, handler) => {
    const fieldState = getFieldState(fieldId);
    if (!fieldState) {
      return;
    }
    let prevHasError = getFieldProp(fieldId, "hasError");
    let prevErrorMsg = getFieldProp(fieldId, "hasError");

    const innerHandler = () => {
      const hasError = getFieldProp(fieldId, "hasError");
      const errorMsg = getFieldProp(fieldId, "errorMsg");
      if (prevHasError === hasError && prevErrorMsg === errorMsg) return;
      prevHasError = hasError;
      prevErrorMsg = errorMsg;
      handler(hasError, errorMsg);
    };

    return fieldState.onChange(debounce(innerHandler, 800));
  };

  const onGlobalErrorMsgChange = (fieldId, handler) => {
    const fieldState = getFieldState(fieldId);
    if (!fieldState) {
      return;
    }
    let prevErrorMsg = getFieldProp(fieldId, "globalErrorMsg");

    const innerHandler = () => {
      const globalErrorMsg = getFieldProp(fieldId, "globalErrorMsg");
      if (prevErrorMsg === globalErrorMsg) return;
      prevErrorMsg = globalErrorMsg;
      handler(!!globalErrorMsg, globalErrorMsg);
    };

    return fieldState.onChange(debounce(innerHandler, 800));
  };

  /**
   * 字段是否校验失败
   * @param {string} fieldId 字段id
   */
  const hasFieldError = (fieldId, simple = true, triggerValid = false) => {
    const state = getFieldState(fieldId);
    // 先判断是否有报错，如果有报错直接返回，如果没有报错则调用前端校验
    const { hasError, errorMsg } = (state && state.toJS()) || {};
    let validateResult;
    if (hasError) {
      validateResult = { hasError, errorMsg };
    } else if (triggerValid) {
      validateResult = state && state.invokeValidate();
    }
    if (simple && validateResult) {
      return validateResult.hasError;
    }
    return validateResult;
  };

  /**
   * 获取字段的 props 属性
   * @param {string} fieldId 字段id
   * @param {string} propName prop属性名
   */
  const getFieldProp = (fieldId, propName, convertFlag = false) => {
    const state = getFieldState(fieldId);
    if (!state) {
      return null;
    }
    if (propName === "value") {
      return getFieldValue(fieldId, convertFlag);
    } else if (propName === "options") {
      return state.get("dataSource").options;
    }
    return state.getData(propName);
  };

  /**
   * 设置字段的 props 属性
   * @param {string} fieldId 字段id
   * @param {object} propName prop属性名
   * @param {any} propValue prop属性值
   * @param {object} option 选项
   */
  const setFieldProp = (fieldId, propName, propValue, option = {}) => {
    const state = getFieldState(fieldId);
    if (!state) {
      return;
    }
    const { fieldsShouldReset = [], dataShare = false } = option;
    const componentName = getComponentName(fieldId);

    state.disableValid();

    // fix 通过联动关系设置脱敏字段，需要将字段去掉字段除非空之外的校验规则
    if (
      isString(propValue) &&
      propName === "value" &&
      propValue.indexOf("*") !== -1
    ) {
      if (componentName === "Input" || componentName === "Textarea") {
        setDesensitizationValidation(
          fieldId,
          getFieldProp(fieldId, "validation")
        );
      }
    }

    // fix 如果联动隐藏组，则需要同时隐藏导航栏
    if (componentName === "GroupLayout" && propName === "visible") {
      const prevGroup = context.getGlobal("groups");
      const groupIndex = findIndex(
        prevGroup,
        (group) => group.fieldId === fieldId
      );
      if (groupIndex > -1) {
        const groups = update(prevGroup, {
          [groupIndex]: { hideOnNav: { $set: !propValue } },
        });
        context.setGlobal({ groups });
        context.emitter.emit("reportGroups", groups);
      }
    }

    if (propName.indexOf('[') !== -1) {
      const str = propName.replace("]", '').replace(/'/g, '');
      const [mainPropName, subPropName] = str.split('[');
      const fieldProp = getFieldProp(fieldId);
      state.set(mainPropName, {
        ...(fieldProp[mainPropName] || {}),
        [subPropName]: propValue,
      });
    } else if (propName.indexOf(".") === -1) {
      if (propName === "value") {
        setFieldValue(fieldId, propValue, { dataShare });
        // 选项列表赋值为 undefined 时，报错
      } else if (propName === "options") {
        state.set(propName, propValue || []);
        // 首项赋值
        if (
          propValue &&
          propValue.length === 1 &&
          ["Select", "Radio"].indexOf(componentName) > -1
        ) {
          setFieldValue(fieldId, propValue[0]);
        }
      } else if (propName === "validation") {
        state.setValidation(propValue);
      } else if (propName === "behavior") {
        const realProps = calculateBehavior(propValue);
        !isEmpty(realProps) && state.setData(realProps, true);
      } else {
        state.set(propName, propValue);
      }

      // 更新 schemaMap 是否能通过 visual 缓存读取？
      if (
        fieldsShouldReset.includes(fieldId) &&
        (propName === "options" || propName === "asyncOptions")
      ) {
        const value = state.getVal();
        const valueInOptions = isValueInOptions(
          value,
          propValue,
          componentName
        );
        // SearchInput组件允许用户输入值不在给定的下拉数据中，不用重置field
        if (
          componentName !== "SearchInput" &&
          (!propValue || !propValue.length || !valueInOptions)
        ) {
          resetField(fieldId);
        }
      }
    } else {
      const [mainPropName, subPropName] = propName.split(".");
      if (mainPropName === "dataSource" && subPropName === "options") {
        state.set("options", propValue || []);
        if (
          propValue &&
          propValue.length === 1 &&
          ["Select", "Radio"].indexOf(componentName) > -1
        )
          setFieldValue(fieldId, propValue[0], { dataShare });
      } else {
        state.set(mainPropName, {
          [subPropName]: propValue,
        });
      }

      if (
        fieldsShouldReset.includes(fieldId) &&
        mainPropName === "dataSource"
      ) {
        const value = state.getVal();
        const valueInOptions = isValueInOptions(
          value,
          propValue,
          componentName
        );
        // SearchInput组件允许用户输入值不在给定的下拉数据中，不用重置field
        if (
          componentName !== "SearchInput" &&
          (!propValue || !propValue.length || !valueInOptions)
        ) {
          resetField(fieldId);
        }
      }
    }

    state.enableValid();
  };

  /**
   * 批量设置字段的 props
   * @param {string} fieldId 字段id
   * @param {object} props props
   * @param {object} option 选项
   */
  const setFieldProps = (fieldId, props, option = {}) => {
    Object.keys(props).forEach((propName) => {
      setFieldProp(fieldId, propName, props[propName], option);
    });
  };

  /**
   * 更新校验条件
   * @param {string} fieldId 字段id
   * @param {array} rules 校验规则
   */
  const setValidation = (fieldId, rules, force, keepError) => {
    const state = getFieldState(fieldId);
    if (keepError) {
      state.validation && state.validation.setRules(rules);
    } else {
      state.setValidation(rules);
    }
    force && state.forceValid();
  };

  /**
   * 更新脱敏字段校验条件
   * @param {string} fieldId 字段id
   * @param {array} rules 校验规则
   */
  const setDesensitizationValidation = (fieldId, rules) => {
    if (validationMap[fieldId]) {
      return;
    }
    validationMap[fieldId] = rules;
    setValidation(
      fieldId,
      (validationMap[fieldId] || []).filter(
        (v) => v.type === "required" || v.type === "emptyRule"
      )
    );

    // 此处需要记录 是否已经绑定联动回调，已经绑定不需要再次绑定
    // 值被清空以后，重置校验规则
    onValueChange(
      fieldId,
      ({ value } = {}) => {
        if (!value || value.indexOf("*") === -1) {
          setValidation(fieldId, validationMap[fieldId]);
        }
      },
      { ignoreError: true }
    );
  };

  const getTime = () => {
    return performance.now ? performance.now() : Date.now();
  };

  const scrollToTop = (offsetTop, duration = 300) => {
    const initialY =
      document.documentElement.scrollTop || document.body.scrollTop;
    const navEl = document.getElementById("govFormNav");
    const navHeight = (navEl && navEl.offsetHeight) || 0;
    const difference = offsetTop - navHeight; // offsetTop - initialY;
    const startTime = getTime();

    function step() {
      let normalizedTime = (getTime() - startTime) / duration;
      if (normalizedTime > 1) normalizedTime = 1;

      window.scrollTo(
        0,
        initialY + difference * Math.sin((normalizedTime * Math.PI) / 2)
      );
      if (normalizedTime < 1) window.requestAnimationFrame(step);
    }
    window.requestAnimationFrame(step);
  };

  // 获取所有的bi a
  const getFieldsState = () => {
    const { content } = context.store.getFormData(null, false) || {};
    return content;
  };

  /**
   * 获取表单数据
   * @param {object} option 选项
   */
  const getFieldsData = (option = {}) => {
    disableEventHandler = true;
    const { disableValid = false, scroll = true, fieldIds } = option;
    const formData = {};
    const addonsData = {};
    const errorMsg = [];
    const errorFieldIds = [];
    let firstTop;
    context.store.getForm().items.forEach((state) => {
      if (fieldIds && !fieldIds.includes(state.getValue("fieldId"))) return;
      if (!state || state.getData("disableCollect")) return; // 滤除无须收集数据的字段
      if (!disableValid && !state.getValue("hasError")) {
        state.forceValid(); // 逐个字段校验
      }
      const data = freezeStateData(state.mutableStates, state, true);

      const {
        label,
        fieldId: fid,
        hasError,
        errorMsg: msg,
        visible,
        fieldData,
        globalErrorMsg,
      } = data;
      // if (!isFieldExist(fid) || getFieldProp(fid, "disableCollect")) return; // 滤除无须收集数据的字段

      // 如果是多页面提交的模式，滤除不需要收集的字段
      if (fieldIds && !fieldIds.includes(fid)) return;

      if (hasError || !isEmpty(globalErrorMsg)) {
        errorFieldIds.push(fid);
        if (!visible) {
          // 隐藏字段报错，抛出到外围
          let realMsg = msg;
          if (realMsg && !realMsg.includes(label)) {
            realMsg = `${label}${realMsg.replace(/^(当前字段)/, "")}`;
          }
          errorMsg.push(`[隐藏字段] ${realMsg}`);
        } else {
          // 非隐藏字段报错，触发滚动
          firstTop = getFirstFieldOffsetTop(fid, firstTop);
        }
      }
      // 数据 转换
      formData[fid] = getFieldValue(fid);
      addonsData[fid] = omit(fieldData, ["value", "dataType"]);
    });

    context.setGlobal({ errorFieldIds });

    // 如果报错 fieldId 不为空
    if (!isEmpty(errorFieldIds)) {
      // 获取字段的报错信息，比较 top 值并滚动
      if (firstTop !== undefined && scroll) {
        scrollToErrorField(firstTop);
      }

      disableEventHandler = false;
      return {
        success: false,
        errorMsg,
        content: formData,
        addonsData,
      };
    } else {
      disableEventHandler = false;
      return {
        success: true,
        content: formData,
        addonsData,
      };
    }
  };

  /**
   * 获取表单数据
   * @param {object} option 选项
   */
  const submit = (option = {}, cb) => {
    const data = getFieldsData(option);
    return cb && cb(data);
  };

  const getFieldRect = (fieldId) => {
    const fieldInstance = getComponentInstance(fieldId);
    // eslint-disable-next-line react/no-find-dom-node
    const fieldNode = findDOMNode(fieldInstance);
    return fieldNode && fieldNode.getBoundingClientRect();
  };

  const getFirstFieldOffsetTop = (fieldId, scrollTop) => {
    let top = getFieldRect(fieldId).top;
    if (scrollTop !== undefined && scrollTop < top) {
      top = scrollTop;
    }
    return top;
  };

  /** 滚动到组，判断组下面是否有错误字段，如果有错误字段，则滚动到错误字段 */
  const scrollToGroup = (groupId) => {
    //
    const group =
      context.getGlobal("groups").find((item) => item.fieldId === groupId) ||
      {};
    const errorFieldIds = context.getGlobal("errorFieldIds");
    const groupErrorChildIds = intersection(group.children, errorFieldIds);
    let top;
    if (groupErrorChildIds.length > 0) {
      top = getFieldRect(groupErrorChildIds[0]).top;
    } else {
      top = getFieldRect(groupId).top;
    }
    scrollToErrorField(top);
  };

  /** 滚动到组，判断组下面是否有错误字段，如果有错误字段，则滚动到错误字段 */
  const scrollToField = (fieldId) => {
    //
    const fieldRect = getFieldRect(fieldId);
    if (fieldRect && !isNil(fieldRect.top)) {
      scrollToErrorField(fieldRect.top);
    }
  };

  const scrollToErrorField = (scrollTop) => {
    let firstTop;

    if (scrollTop !== undefined) {
      firstTop = scrollTop;
    } else {
      const errorFieldIds = [];
      context.store.getForm().items.forEach((state) => {
        const data = freezeStateData(state.mutableStates, state, true);

        const { fieldId: fid, hasError } = data;
        if (getFieldProp(fid, "disableCollect")) return; // 滤除无须收集数据的字段

        if (hasError) {
          errorFieldIds.push(fid);
          firstTop = getFirstFieldOffsetTop(fid, firstTop);
        }
      });
      context.setGlobal({ errorFieldIds });
    }
    if (firstTop !== undefined) {
      scrollToTop(firstTop);
    }
  };

  const triggerFieldEvent = (fieldId, eventName, params) => {
    const state = getFieldState(fieldId);
    if (!state || !state.emitter) {
      return;
    }
    state.emitter.emit(eventName, params);
    return Promise.race([
      new Promise((resolve) => setTimeout(resolve, 5000, "timeout")),
      new Promise((resolve) =>
        state.emitter.once(`${eventName}:end`, () => {
          resolve("success");
        })
      ),
    ]);
  };

  const turnPages = (currentPage, pages) => {
    pages.forEach((id, index) => {
      const fieldInstance = getComponentInstance(id);

      // eslint-disable-next-line react/no-find-dom-node
      const fieldNode = findDOMNode(fieldInstance);
      if (index !== currentPage) {
        fieldNode?.classList.add("hide");
      } else {
        fieldNode?.classList.remove("hide");
      }
    });
  };

  const getErrorMsg = ({ triggerValidate = false } = {}) => {
    const errors = [];
    let formHasInput = false;
    context.store.getForm().items.forEach((state) => {
      if (triggerValidate && !state.getValue("hasError")) {
        state.forceValid();
      }
      const data = (state && state.toJS()) || {};
      const {
        fieldId,
        hasError,
        label,
        errorMsg,
        visible,
        readonly,
        disabled,
        dataShare,
        fieldData,
      } = data;
      const hasInput = fieldData?.dataType === "CHANGED";
      if (hasInput) formHasInput = true;
      if (fieldId && hasError) {
        errors.push({
          fieldId,
          hasError,
          label,
          errorMsg,
          visible,
          readonly,
          disabled,
          dataShare,
          hasInput,
        });
      }
    });
    const hasInput = formHasInput;
    const hasSaved = !!context.getGlobal()?.hasSaved;
    return {
      errors,
      hasInput,
      hasSaved,
    };
  };

  const bindFieldEvent = (fieldId, eventName, callback) => {
    const trigger = `${fieldId}:${eventName}`;
    const wrappedCallback = debounce(callback, 200);
    context.store.emitter.on(trigger, wrappedCallback);
    return () => {
      context.store.emitter.off(trigger, wrappedCallback);
    };
  };

  return {
    isFieldExist,
    getFieldState,
    getFieldValue,
    getComponentName,
    getComponentSchema,
    setFieldValue,
    resetField,
    onValueChange,
    hasFieldError,
    getFieldProp,
    setFieldProp,
    setFieldProps,
    setValidation,
    getFieldsData,
    getFieldsState,
    submit,
    scrollToErrorField,
    getFieldRect,
    scrollToGroup,
    turnPages,
    onValidationChange,
    onGlobalErrorMsgChange,
    triggerFieldEvent,
    scrollToField,
    freezeStateData,
    getErrorMsg,
    bindFieldEvent,
  };
};
